// Overview hypothesis + stage editor. View mode shows the hypothesis text;
// Edit reveals a textarea + stage select that round-trip through zou's
// UpdateCaptureProject. A converted capture is read-only (stage transitions out
// of converted are owned by the conversion flow, not here).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { STAGES } from './constants';
import { useUpdateCaptureProject } from './hooks';
export function HypothesisCard({ captureProjectId, summary }) {
    const update = useUpdateCaptureProject();
    const [editing, setEditing] = useState(false);
    const [hypothesis, setHypothesis] = useState(summary.opportunityHypothesis);
    const [stage, setStage] = useState(summary.stage);
    // Resync from the server whenever we're not actively editing (e.g. after a
    // save refetch, or if the summary changes underneath us).
    useEffect(()=>{
        if (!editing) {
            setHypothesis(summary.opportunityHypothesis);
            setStage(summary.stage);
        }
    }, [
        summary.opportunityHypothesis,
        summary.stage,
        editing
    ]);
    const converted = summary.stage === 'converted';
    const save = ()=>update.mutate({
            captureProjectId,
            name: summary.name,
            stage,
            opportunityHypothesis: hypothesis,
            accountId: summary.accountId,
            customerOrgId: summary.customerOrgId
        }, {
            onSuccess: ()=>setEditing(false)
        });
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "flex flex-row items-center justify-between space-y-0 py-4",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        children: "Opportunity hypothesis"
                    }),
                    !editing && !converted && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        size: "sm",
                        "data-testid": Testids.capture.hypothesisEditButton,
                        onClick: ()=>setEditing(true),
                        iconLeft: /*#__PURE__*/ _jsx(Pencil, {
                            className: "h-3.5 w-3.5"
                        }),
                        children: "Edit"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraCardContent, {
                className: "space-y-3",
                children: editing ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: hypothesis,
                            onChange: (e)=>setHypothesis(e.target.value),
                            rows: 4,
                            placeholder: "Why we think we can win — incumbent weaknesses, our discriminators, customer hot buttons.",
                            "data-testid": Testids.capture.hypothesisInput
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-end gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "text-xs font-medium",
                                            children: "Stage"
                                        }),
                                        /*#__PURE__*/ _jsxs(Select, {
                                            value: stage,
                                            onValueChange: setStage,
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                                    className: "w-[180px]",
                                                    "data-testid": Testids.capture.stageSelect,
                                                    children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                }),
                                                /*#__PURE__*/ _jsx(SelectContent, {
                                                    children: STAGES.map((s)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: s.value,
                                                            children: s.label
                                                        }, s.value))
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "ml-auto flex gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "secondary",
                                            size: "sm",
                                            onClick: ()=>setEditing(false),
                                            children: "Cancel"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            onClick: save,
                                            disabled: update.isPending,
                                            "data-testid": Testids.capture.hypothesisSaveButton,
                                            children: update.isPending ? 'Saving…' : 'Save'
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }) : /*#__PURE__*/ _jsx("p", {
                    className: "text-sm",
                    children: summary.opportunityHypothesis || 'No hypothesis captured yet.'
                })
            })
        ]
    });
}
